import { useEffect, useRef, useState } from "react";
import { Col, message, Row } from "antd";
import { useDispatch } from "react-redux";

import Table from "@components/Table/index";
import {
  ErrorServices,
  gradeSetupApi,
  useDeleteJobGradeMutation,
  useFetchCompanyJobGradesQuery,
  useUploadJobGradesMutation,
} from "@services";
import { useTypedSelector } from "@hooks";
import { getColumns } from "./config";
import AddGrade from "./AddGrade";
import GradeSetupVersions from "./Versions";
import { IJobGrade } from "@types";
import Upload from "@components/Upload";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import CustomButton from "@/components/customButton";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";
import SearchBox from "@/components/searchInput";
import customErrorMessage from "@/components/CustomErrorMessage";
import UploadButton from "@/components/UploadButton";
import useDebounce from "@/hooks/useDebounce";

const GradeSetup = () => {
  const { company, role } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc" | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);

  let jobGrade_id = useRef<any>(null);
  const { data: jobGradesData, isLoading } = useFetchCompanyJobGradesQuery({
    company_id,
    page,
    page_size: pageSize,
    sort_by: sortBy,
    order,
    search: debouncedSearch,
  });
  const { data, pagination } = jobGradesData || {};
  const [deleteJobGrade, { isLoading: isDeleting }] =
    useDeleteJobGradeMutation();
  const [isVisible, setIsVisible] = useState(false);
  const [isVersionsModal, setIsVersionsModal] = useState(false);

  const [isUploadModal, setIsUploadModal] = useState(false);
  const [selectedJobGrade, setSelectedJobGrade] = useState<null | IJobGrade>(
    null
  );
  const [uploadFile, setUploadFile] = useState({
    file: null,
    active: false,
  });

  const [uploadJobGrades, { isLoading: isUploading }] =
    useUploadJobGradesMutation();

  const removeJobGrade = async (id: number) => {
    try {
      jobGrade_id.current = id;

      const res = await deleteJobGrade({
        id,
        company_id,
      }).unwrap();

      message.success(res?.message || "Job Grade deleted successfully!");
    } catch (error: any) {
      customErrorMessage(
        error?.data?.message ||
        error?.message ||
        "Failed to delete Job Grade"
      );
    }
  };

  const editJobGrade = (jobGrade: IJobGrade) => {
    setSelectedJobGrade(jobGrade);
    setIsVisible(true);
  };

  const handleUpload = async () => {
    const { file, active }: { file: any; active: boolean } = uploadFile;
    setIsUploadModal(false);
    try {
      const formData = new FormData();
      formData.append("attachment", file!, file?.name!);
      const res = await uploadJobGrades({
        company_id,
        active,
        body: formData,
      }).unwrap();
      message.success(res?.message);
    } catch (error) {
      customErrorMessage(error?.message);
    }
  };

  const download = async (id?: number) => {
    try {
      const result: any = await dispatch(
        gradeSetupApi.endpoints.downloadJobGrades.initiate(
          {
            company_id,
            version_id: id ?? null,
          },
          {
            subscribe: false,
            forceRefetch: true,
          }
        )
      );

      if (result?.error) {
        customErrorMessage(
          result?.error?.data?.message ||
          result?.error?.message ||
          "Failed to download file"
        );
      }
    } catch (error: any) {
      customErrorMessage(error?.message || "Failed to download file");
    }
  };

  const columns = getColumns({
    deleteJobGrade: removeJobGrade,
    jobGrade_id,
    isDeleting,
    editJobGrade,
    userRole: role,
  });

  return (
    <>
      {isVisible && (
        <AddGrade
          selectedJobGrade={selectedJobGrade}
          setSelectedJobGrade={setSelectedJobGrade}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
        />
      )}

      {isVersionsModal && (
        <GradeSetupVersions
          isVisible={isVersionsModal}
          setIsVisible={setIsVersionsModal}
          download={download}
        />
      )}
      {isUploadModal && (
        <Upload
          title="Upload Job Grades"
          onSubmit={handleUpload}
          isVisible={isUploadModal}
          setIsVisible={setIsUploadModal}
          file={uploadFile}
          setFile={setUploadFile}
        />
      )}

      <div className="bg-white p-4 sm:p-5">
        <div className="jobCreationBar bg-revampBlue px-4 py-6 rounded-lg flex justify-between items-center ">
          <div className="title text-white  font-medium text-3xl uppercase">
            Grade Setup
          </div>

          <div className="flex flex-row justify-between">
            <UploadButton
              role={role}
              createPermission={permissions.CREATE_JOB_GRADE}
              updatePermission={permissions.UPDATE_JOB_GRADE}
              onClick={() => setIsUploadModal(true)}
              isLoading={isUploading}
            />

            <CustomButton
              onClick={() => download()}
              btnName="Download"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
              iconPosition="right"
            >
              {/* <MdOutlineFileDownload
                size={20}
                className="text-black font-bold"
              /> */}
            </CustomButton>

            <CustomButton
              onClick={() => setIsVersionsModal(true)}
              btnName="Versions"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
              iconPosition="left"
            ></CustomButton>

            {checkPermission(permissions.CREATE_JOB_GRADE) && (
              <CustomButton
                onClick={() => setIsVisible(true)}
                btnName="Create Job Grade"
                customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                iconPosition="left"
              ></CustomButton>
            )}

            <SearchBox
              placeholder="Type to search..."
              value={searchInput}
              onChange={value => {
                setSearchInput(value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="jobCreationListing bg-white px-5 py-4 rounded-lg">


        <Table
          data={data}
          isLoading={isLoading}
          columns={columns}
          pagination={true}
          count={pagination?.count}
          onChangePage={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          onTableChange={(pagination, filters, sorter) => {
            if (sorter?.field) {
              setSortBy(sorter.field);
              setOrder(sorter.order === "ascend" ? "asc" : "desc");
              setPage(1);
            }
          }}
        />
      </div>
    </>
  );
};

export default GradeSetup;
